import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FaFileInvoiceDollar,
    FaDownload,
    FaEye,
    FaShareAlt,
    FaSearch,
    FaFilter,
    FaHistory,
    FaTimes
} from 'react-icons/fa';
import { showSuccess, showError } from '../../../utils/Alert';
import Loader from '../../shared/Loader';
import './StudentInvoices.css';

const StudentInvoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    useEffect(() => {
        fetchInvoices();
    }, []);

    const fetchInvoices = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get('/api/student/invoices', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setInvoices(res.data);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching invoices", err);
            setLoading(false);
        }
    };

    const handleDownload = (invoice) => {
        try {
            const data = [
                ["Invoice No", invoice.invoiceNo],
                ["Payment ID", invoice.paymentId],
                ["Date", new Date(invoice.date).toLocaleString()],
                ["Course", invoice.courseId?.title],
                ["Category", invoice.courseId?.category],
                ["Amount", `$${invoice.amount}`],
                ["Status", invoice.status]
            ];

            const csvContent = data.map(row => row.join(",")).join("\n");
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Invoice_${invoice.invoiceNo}.csv`);
            document.body.appendChild(link);
            link.click();

            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 150);

            showSuccess("Download Complete", `Receipt for ${invoice.invoiceNo} has been saved.`);
        } catch (err) {
            console.error(err);
            showError("Download Failed", "Could not generate invoice receipt.");
        }
    };

    const handleShare = (invoice) => {
        if (navigator.share) {
            navigator.share({
                title: `Invoice ${invoice.invoiceNo}`,
                text: `Payment confirmation for ${invoice.courseId?.title}`,
                url: window.location.href
            }).catch(console.error);
        } else {
            showSuccess("Link Copied", "Invoice reference link copied to clipboard.");
        }
    };

    const filteredInvoices = invoices.filter(inv =>
        inv.invoiceNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.courseId?.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <Loader />;

    return (
        <div className="student-invoices fade-in">
            <div className="invoice-header">
                <div className="header-info">
                    <h1 className="page-title"><FaFileInvoiceDollar /> Billing & Invoices</h1>
                    <p className="page-subtitle">Track your platform investments and download your course receipts.</p>
                </div>
            </div>

            <div className="invoice-controls">
                <div className="search-bar">
                    <FaSearch className="search-icon" />
                    <input
                        type="text"
                        placeholder="Search by Invoice No or Course..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
                <div className="history-badge">
                    <FaHistory /> {invoices.length} Total Transactions
                </div>
            </div>

            <div className="invoice-table-container">
                <table className="invoice-table">
                    <thead>
                        <tr>
                            <th>Invoice No</th>
                            <th>Course / Product</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredInvoices.map((inv) => (
                            <tr key={inv._id}>
                                <td className="inv-no">{inv.invoiceNo}</td>
                                <td className="inv-course">
                                    <div className="course-chip">{inv.courseId?.category || 'Premium'}</div>
                                    <span>{inv.courseId?.title}</span>
                                </td>
                                <td className="inv-date">{new Date(inv.date).toLocaleDateString()}</td>
                                <td className="inv-amount">${inv.amount}</td>
                                <td className="inv-status">
                                    <span className={`status-pill ${inv.status.toLowerCase()}`}>
                                        {inv.status}
                                    </span>
                                </td>
                                <td className="inv-actions">
                                    <button className="action-btn view" onClick={() => setSelectedInvoice(inv)} title="View Receipt">
                                        <FaEye />
                                    </button>
                                    <button className="action-btn download" onClick={() => handleDownload(inv)} title="Download PDF">
                                        <FaDownload />
                                    </button>
                                    <button className="action-btn share" onClick={() => handleShare(inv)} title="Share">
                                        <FaShareAlt />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {filteredInvoices.length === 0 && (
                    <div className="empty-state">
                        <FaFileInvoiceDollar size={48} />
                        <h3>No invoices found</h3>
                        <p>You haven't made any premium purchases yet.</p>
                    </div>
                )}
            </div>

            {/* --- Invoice Detail Modal --- */}
            {selectedInvoice && (
                <div className="invoice-modal-overlay" onClick={() => setSelectedInvoice(null)}>
                    <div className="invoice-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>Invoice Reference: {selectedInvoice.invoiceNo}</h2>
                            <button className="modal-close-btn" onClick={() => setSelectedInvoice(null)}><FaTimes /></button>
                        </div>
                        <div className="modal-body">
                            <div className="receipt-box">
                                <div className="receipt-header">
                                    <div className="brand">ZENX STAFFING SOLUTIONS</div>
                                    <div className="receipt-type">PAYMENT RECEIPT</div>
                                </div>
                                <div className="receipt-details">
                                    <div className="d-row"><strong>Date:</strong> {new Date(selectedInvoice.date).toLocaleString()}</div>
                                    <div className="d-row"><strong>Payment ID:</strong> {selectedInvoice.paymentId}</div>
                                    <div className="d-row"><strong>Service:</strong> {selectedInvoice.courseId?.title}</div>
                                    <div className="d-row"><strong>Category:</strong> {selectedInvoice.courseId?.category}</div>
                                </div>
                                <div className="receipt-total">
                                    <span>Total Paid:</span>
                                    <strong>${selectedInvoice.amount}</strong>
                                </div>
                                <div className="receipt-footer">
                                    This is a computer-generated receipt. No signature required.
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button className="print-btn" onClick={() => window.print()}>Print Receipt</button>
                            {/* <button className="close-btn" onClick={() => setSelectedInvoice(null)}>Close</button> */}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentInvoices;
